import { useMemo } from "react";
import { useField } from "formik";
import {
    useScheduleTemplate,
    useScheduleTemplates,
} from "@api/scheduleTemplate.api";
import { Picker } from "@design-system/Picker/Picker.lib";
import { Checkbox } from "@design-system/Checkbox/Checkbox";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import type { TriggerButtonProps } from "@design-system/overlay/triggerButtonProps";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { TransactionRuleActionFormSchema } from "../constants";
import { getEligibleScheduleTemplates } from "./scheduleRuleEligibility";
import styles from "./ActionForm.module.scss";

const SCHEDULE_PLACEHOLDER = "Select schedule";

function isScheduleAutoConfirm(
    value: TransactionRuleActionFormSchema["value"] | undefined,
): boolean {
    return Boolean(value?.autoPostOnApply || value?.autoPostRecognition);
}

interface Props {
    index: number;
    requiredEntityIds: number[];
    controlButtonSize?: TriggerButtonProps["size"];
}

export const ScheduleActionForm: React.FC<Props> = ({
    index,
    requiredEntityIds,
    controlButtonSize,
}) => {
    const [field, , helpers] = useField<
        TransactionRuleActionFormSchema["value"]
    >(`actions.${index}.value`);
    const templatesQuery = useScheduleTemplates();
    const { schedulesExpenses: isExpensesEnabled } = useTypedFlags();
    const selectedTemplateId = field.value?.scheduleTemplateId ?? null;
    const selectedTemplateQuery = useScheduleTemplate(selectedTemplateId);
    const templateItems = useMemo(
        () =>
            getEligibleScheduleTemplates({
                templates: templatesQuery.data ?? [],
                requiredEntityIds,
                isExpensesEnabled,
            }).map((template) => ({
                value: template.id,
                label: template.name,
            })),
        [isExpensesEnabled, requiredEntityIds, templatesQuery.data],
    );

    const selectedTemplateName =
        templateItems.find((item) => item.value === selectedTemplateId)
            ?.label ?? selectedTemplateQuery.data?.name;

    return (
        <>
            <Picker.Select
                items={templateItems}
                searchable
                value={selectedTemplateId}
                onValueChange={(templateId) => {
                    if (templateId !== null) {
                        const isAutoConfirm = isScheduleAutoConfirm(
                            field.value,
                        );
                        helpers.setValue({
                            scheduleTemplateId: templateId,
                            autoPostOnApply: isAutoConfirm,
                            autoPostRecognition: isAutoConfirm,
                        });
                    }
                }}
                renderValue={() => selectedTemplateName ?? SCHEDULE_PLACEHOLDER}
                placeholder={SCHEDULE_PLACEHOLDER}
                searchPlaceholder="Search policies..."
                emptyLabel="No matching policies"
                trigger={
                    <Picker.Button
                        variant="primary"
                        size={controlButtonSize ?? "md"}
                    />
                }
            />
            <ScheduleAutoPostCheckbox
                label="Auto confirm"
                tooltip="Automatically post transactions that meet rule conditions to the accrual ledger"
                checked={isScheduleAutoConfirm(field.value)}
                onCheckedChange={(checked) =>
                    helpers.setValue({
                        ...field.value,
                        autoPostOnApply: checked,
                        autoPostRecognition: checked,
                    })
                }
            />
        </>
    );
};

function ScheduleAutoPostCheckbox({
    label,
    tooltip,
    checked,
    onCheckedChange,
}: {
    label: string;
    tooltip: string;
    checked: boolean;
    onCheckedChange: (checked: boolean) => void;
}) {
    return (
        <Tooltip content={tooltip}>
            <span className={styles.autoConfirmCheckbox}>
                <Checkbox
                    checked={checked}
                    label={
                        <span className={styles.autoConfirmLabel}>{label}</span>
                    }
                    onCheckedChange={onCheckedChange}
                />
            </span>
        </Tooltip>
    );
}
